import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, Filter, ExternalLink, Mail, Linkedin,
  CheckCircle2, Star, Sparkles, RefreshCw, ChevronLeft, ChevronRight, Users, Flame
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 12;

function Avatar({ profile, size = 56 }) {
  const initials = (profile.nom_prenom || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  if (profile.photo) {
    return (
      <img src={`${API}${profile.photo}`} alt={profile.nom_prenom} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }}
        onError={(e) => { e.target.style.display = "none"; }} />
    );
  }
  return (
    <div className="cbm-avatar flex-shrink-0 text-gold font-outfit font-bold"
      style={{ width: size, height: size, fontSize: size * 0.33, background: "linear-gradient(135deg, #132040, #0D1B3E)" }}>
      {initials}
    </div>
  );
}

function MemberCard({ profile, index }) {
  const navigate = useNavigate();
  return (
    <div
      className={`cbm-card p-5 flex flex-col animate-fade-in-up-delay-${Math.min(index % 6 + 1, 6)}`}
      data-testid={`member-card-${profile._id}`}
      style={{ minHeight: 340 }}
    >
      {/* 1. Avatar + Nom + vérifié */}
      <div className="flex items-center gap-3 mb-3">
        <Avatar profile={profile} size={52} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3
              className="font-outfit text-white leading-tight truncate"
              style={{ fontSize: "1.125rem", fontWeight: 700, letterSpacing: "-0.01em" }}
            >
              {profile.nom_prenom}
            </h3>
            {profile.is_verified && <CheckCircle2 size={14} className="text-green-400 flex-shrink-0" />}
          </div>
        </div>
      </div>

      {/* 2. Fonction (hauteur réservée) */}
      <div style={{ minHeight: 18 }}>
        {profile.fonction && (
          <p
            className="truncate"
            style={{ color: "#C9A227", fontWeight: 600, fontSize: "0.8125rem" }}
            data-testid={`member-fonction-${profile._id}`}
          >
            {profile.fonction}
          </p>
        )}
      </div>

      {/* 3. Entreprise (hauteur réservée) */}
      <div className="mt-0.5" style={{ minHeight: 22 }}>
        {profile.entreprise && (
          <p className="truncate" style={{ color: "#E6EDF7", fontWeight: 500, fontSize: "0.9375rem" }}>
            {profile.entreprise}
          </p>
        )}
      </div>

      {/* 4. Secteur · Ville (texte simple neutre, hauteur réservée) */}
      <div className="mt-1" style={{ minHeight: 20 }}>
        <p className="truncate" style={{ color: "#AFC3E0", fontSize: "0.8125rem", fontWeight: 500 }}>
          {[profile.secteur, profile.ville].filter(Boolean).join(" • ") || " "}
        </p>
      </div>

      {/* 5. Badges (hauteur fixe, pas de wrap) */}
      <div
        className="flex items-center gap-1.5 mt-3 overflow-hidden"
        style={{ height: 22, flexWrap: "nowrap" }}
      >
        {profile.is_ambassador && <span className="cbm-badge cbm-badge-ambassador whitespace-nowrap" data-testid={`badge-ambassador-${profile._id}`}><Sparkles size={9} className="mr-0.5" />Ambassadeur</span>}
        {profile.is_top_connector && <span className="cbm-badge cbm-badge-top-connector whitespace-nowrap" data-testid={`badge-top-connector-${profile._id}`}><Flame size={9} className="mr-0.5" />Top connecteur</span>}
        {profile.is_recommended && <span className="cbm-badge cbm-badge-recommended whitespace-nowrap"><Sparkles size={9} className="mr-0.5" />Recommandé</span>}
        {profile.is_new && <span className="cbm-badge cbm-badge-new whitespace-nowrap">Nouveau</span>}
      </div>

      {/* 6. Description (2 lignes, hauteur fixe) */}
      <div className="mt-3" style={{ minHeight: 48 }}>
        {profile.offre && (
          <p className="line-clamp-2" style={{ color: "#CFD9EC", fontSize: "0.9375rem", lineHeight: 1.55 }}>
            {profile.offre}
          </p>
        )}
      </div>

      {/* 7. CTA */}
      <div className="flex items-center gap-2 pt-3 mt-auto">
        <button
          data-testid={`view-profile-${profile._id}`}
          className="cbm-btn-ghost text-sm w-full justify-center py-2.5 border border-white/10 hover:border-white/25 whitespace-nowrap"
          onClick={() => navigate(`/profil/${profile._id}`)}
        >
          <ExternalLink size={14} /> Voir profil
        </button>
      </div>
    </div>
  );
}

function Pagination({ page, pages, total, onChange }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 mt-8 animate-fade-in">
      <button data-testid="prev-page" disabled={page === 1} onClick={() => onChange(page - 1)}
        className="cbm-btn-ghost text-sm border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed py-2 px-3">
        <ChevronLeft size={16} />
      </button>
      <div className="flex items-center gap-1">
        {Array.from({ length: pages }, (_, i) => i + 1).filter(p => Math.abs(p - page) <= 2 || p === 1 || p === pages).map((p, idx, arr) => (
          <>
            {idx > 0 && arr[idx - 1] !== p - 1 && <span key={`ellipsis-${p}`} className="text-cbm-muted text-sm px-1">...</span>}
            <button key={p} data-testid={`page-${p}`} onClick={() => onChange(p)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${p === page ? "bg-gold text-navy font-semibold" : "text-cbm-secondary hover:bg-white/5"}`}>
              {p}
            </button>
          </>
        ))}
      </div>
      <button data-testid="next-page" disabled={page === pages} onClick={() => onChange(page + 1)}
        className="cbm-btn-ghost text-sm border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed py-2 px-3">
        <ChevronRight size={16} />
      </button>
      <span className="text-cbm-muted text-xs">{total} membres</span>
    </div>
  );
}

export default function Annuaire() {
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [secteur, setSecteur] = useState("");
  const [ville, setVille] = useState("");
  const [secteurs, setSecteurs] = useState([]);
  const [villes, setVilles] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchParams] = useSearchParams();

  // Pre-fill from URL query ?search=xxx (used for event CTAs)
  useEffect(() => {
    const q = searchParams.get("search");
    if (q) {
      setSearch(q);
      setSearchInput(q);
    }
  }, [searchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (secteur) params.secteur = secteur;
      if (ville) params.ville = ville;
      const { data } = await axios.get(`${API}/api/profiles`, { params, withCredentials: true });
      setProfiles(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, secteur, ville, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/api/filters/secteurs`, { withCredentials: true }).then(({ data }) => setSecteurs(data)).catch(() => {});
    axios.get(`${API}/api/filters/villes`, { withCredentials: true }).then(({ data }) => setVilles(data)).catch(() => {});
  }, []);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput); };
  const clearFilters = () => { setSearch(""); setSecteur(""); setVille(""); setSearchInput(""); setPage(1); };
  const handlePageChange = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 animate-fade-in-up">
        <div>
          <h1 className="cbm-section-title">Annuaire</h1>
          <p className="text-cbm-muted text-sm mt-1">{total} membre{total > 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="cbm-filter-bar mb-6 animate-fade-in-up-delay-1">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-cbm-muted" />
            <input data-testid="search-input" type="text" className="cbm-input pl-9" placeholder="Rechercher un membre, entreprise, offre..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          <select data-testid="filter-secteur" className="cbm-input sm:w-48" value={secteur} onChange={(e) => { setSecteur(e.target.value); setPage(1); }}>
            <option value="">Tous les secteurs</option>
            {secteurs.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-ville" className="cbm-input sm:w-40" value={ville} onChange={(e) => { setVille(e.target.value); setPage(1); }}>
            <option value="">Toutes les villes</option>
            {villes.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="flex gap-2">
            <button data-testid="search-button" type="submit" className="cbm-btn-primary text-sm px-4 py-2"><Filter size={14} /></button>
            {(search || secteur || ville) && (
              <button type="button" className="cbm-btn-ghost text-sm border border-white/10" onClick={clearFilters} data-testid="clear-filters"><RefreshCw size={14} /></button>
            )}
          </div>
        </form>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="cbm-card p-5 space-y-3">
              <div className="flex gap-3"><div className="cbm-skeleton rounded-full" style={{ width: 52, height: 52 }} /><div className="flex-1 space-y-2"><div className="cbm-skeleton h-4 w-32" /><div className="cbm-skeleton h-3 w-24" /></div></div>
              <div className="cbm-skeleton h-3 w-full" /><div className="cbm-skeleton h-8 w-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-20 animate-fade-in">
          <Users size={40} className="text-cbm-muted mx-auto mb-3" />
          <p className="text-cbm-secondary font-outfit text-lg">Aucun membre trouvé</p>
          <p className="text-cbm-muted text-sm mt-1">Essayez de modifier vos filtres</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {profiles.map((p, i) => <MemberCard key={p._id} profile={p} index={i} />)}
          </div>
          <Pagination page={page} pages={pages} total={total} onChange={handlePageChange} />
        </>
      )}
    </div>
  );
}

